import type { ReactNode } from "react";
import { StyleSheet, Text, View, useWindowDimensions } from "react-native";

import { Modal } from "./Modal";
import { IconButton } from "./IconButton";
import type { UiTheme } from "../theme";

// Centred dialog chrome over the shared <Modal> scrim: a floating card, fade-in.
// Encodes the dialog shell (elevated surface, border, radius, maxWidth/maxHeight,
// optional title) ONCE for the hand-rolled centred modals (MergeTargetPicker,
// ProgramEditor/Select, the RestStepper dialogs — see next/consolidation.md).
// Body + action row are caller-composed (they vary: TextField+Button,
// stepper+3-action-row, scroll-list+Cancel).
//
// `dismissOnBackdropPress` covers both interaction variants: true = tap-out to
// dismiss; false = force an explicit Cancel so a stray tap can't discard an
// edit (the stepper dialogs / MergeTargetPicker).

export type DialogProps = {
  theme: UiTheme;
  visible: boolean;
  onRequestClose: () => void;
  dismissOnBackdropPress?: boolean;
  /** Optional header title. */
  title?: string;
  /** Card width cap (px). Default 420 (a dialog); larger surfaces (e.g. the
   *  Create-hub wide card) pass a bigger value. */
  maxWidth?: number;
  /**
   * When set, renders a trailing ✕ close button beside the title wired to
   * onRequestClose (the a11y label). Omit for dialogs whose dismiss is a body
   * action row / tap-out.
   */
  closeLabel?: string;
  children: ReactNode;
};

export function Dialog({
  theme,
  visible,
  onRequestClose,
  dismissOnBackdropPress = true,
  title,
  maxWidth = 420,
  closeLabel,
  children,
}: DialogProps) {
  // Reactive viewport height (RNW subscribes to window resize) so the card's
  // height cap follows a browser resize while the dialog is open — the shared
  // useWindowDimensions, not a one-shot Dimensions.get read.
  const { height } = useWindowDimensions();
  const styles = makeStyles(theme, height);
  return (
    <Modal
      theme={theme}
      visible={visible}
      onRequestClose={onRequestClose}
      dismissOnBackdropPress={dismissOnBackdropPress}
      animationType="fade"
      backdropStyle={styles.backdrop}
    >
      <View style={[styles.card, { maxWidth }]}>
        {closeLabel !== undefined ? (
          <View style={styles.header}>
            <Text style={styles.headerTitle} numberOfLines={1}>
              {title ?? ""}
            </Text>
            <IconButton
              theme={theme}
              name="close"
              accessibilityLabel={closeLabel}
              onPress={onRequestClose}
            />
          </View>
        ) : title !== undefined ? (
          <Text style={styles.title} numberOfLines={2}>
            {title}
          </Text>
        ) : null}
        {children}
      </View>
    </Modal>
  );
}

function makeStyles(theme: UiTheme, viewportHeight: number) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    backdrop: {
      justifyContent: "center",
      alignItems: "center",
      padding: s.s5,
    },
    card: {
      // alignSelf centres the card on wide viewports (tablet / web-rnw
      // console): width:100% + maxWidth alone left-aligns once the cap bites.
      // maxWidth is applied inline (prop-driven).
      alignSelf: "center",
      width: "100%",
      // Pixel cap (not "85%") — a percentage maxHeight can't resolve against
      // the auto-height swallow above, so a long list would overflow instead
      // of scroll-capping. A definite value from the reactive viewport height.
      maxHeight: Math.round(viewportHeight * 0.85),
      backgroundColor: p.surface,
      borderRadius: r.r4,
      borderWidth: 1,
      borderColor: p.border,
      padding: s.s5,
      gap: s.s4,
    },
    title: { color: p.ink, fontSize: t.sizes.base, fontWeight: "700" },
    // Header row (title + ✕ close) — used when closeLabel is set.
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: s.s3,
    },
    headerTitle: {
      flexShrink: 1,
      color: p.ink,
      fontSize: t.sizes.lg,
      fontWeight: "700",
    },
  });
}
